import clsx from 'clsx'
import { useContext, useEffect, useState } from 'react'
import { useApplicant } from '~/api/queries/applicant'
import { AsideActionWrapper } from '~/components/Layout/AsideActionWrapper/AsideActionWrapper'
import { applicantModalPictureWrapper } from '~/components/Scopes/Applicants/ApplicantModal/ApplicantModal.css'
import {
  applicantModalAsideActions,
  applicantModalAsideActionsTitle
} from '~/components/Scopes/Applicants/ApplicantModal/components/ApplicantModalActions.css'
import { ApplicantReports } from '~/components/Scopes/Applicants/ApplicantModal/components/ApplicantReports'
import { ActionsButtonList } from '~/domains/actions/components/ActionsButtonList/ActionsButtonList'
import type { TargetActionName } from '~/domains/actions/components/ActionsButtonList/actionsForTarget'
import {
  applicantAccessDeniedAgencies,
  applicantAccessDeniedMessage
} from '~/domains/applicants/accessDenied'
import { ApplicantAvatar } from '~/domains/applicants/components/ApplicantAvatar/ApplicantAvatar'
import { ApplicantStatusUpdater } from '~/domains/applicants/components/ApplicantStatusUpdater'
import { useApplicantHead } from '~/domains/applicants/queries/applicantHead'
import { useApplicantPicture } from '~/domains/applicants/queries/applicantPictures'
import type { ApplicantHeadData } from '~/domains/applicants/types'
import { ApplicantTasks } from '~/domains/tasks/components/ApplicantTasks/ApplicantTasks'
import type { AsideActionPosition } from '~/types/global'
import { ApplicantModalContext } from '~/utils/contextes'
import { formatName } from '~/utils/names'
import { ContextPhoneButton } from './ContextPhoneButton'
import {
  contextAvatar,
  contextHeaderActions,
  contextName,
  contextNameButton,
  contextNameLink
} from './MessagingContext.css'
import { MessagingContextAccessDenied } from './MessagingContextAccessDenied'
import { MessagingContextPanel } from './MessagingContextPanel'

type MessagingApplicantHead = ApplicantHeadData & {
  phoneSecondary?: string | null
}

// The thread has its own composer, and the page has no room for a fourth column
const HIDDEN_ACTIONS: TargetActionName[] = ['message', 'email']
const ACTION_POSITIONS: AsideActionPosition[] = ['bottom']

export const ApplicantContext = ({
  id,
  onToggle,
  onActionOpenChange
}: {
  id: number
  onToggle?: () => void
  onActionOpenChange?: (isOpen: boolean) => void
}) => {
  const { setApplicantCurrentId } = useContext(ApplicantModalContext)
  const { data, isLoading } = useApplicantHead<MessagingApplicantHead>({
    applicantId: id,
    additionalFields: ['status', 'email', 'phoneMobile', 'phoneSecondary']
  })
  const { data: picture } = useApplicantPicture({ applicantId: id })
  // The head comes from the filtered collection: a record the ACL hides is simply absent. The
  // item endpoint then says why (403 with the agencies), the way the applicant modal does.
  const headMissing = !isLoading && !data
  const { failureReason: accessError } = useApplicant({
    id: headMissing ? id : undefined,
    showToast: false
  })
  const [currentAction, setCurrentAction] = useState<string | null>(null)
  const [currentHoverAction, setCurrentHoverAction] = useState<string | null>(
    null
  )

  useEffect(() => {
    onActionOpenChange?.(currentAction !== null)
  }, [currentAction, onActionOpenChange])

  if (isLoading) {
    return <MessagingContextPanel loading onToggle={onToggle} />
  }

  if (!data) {
    return (
      <MessagingContextAccessDenied
        description={applicantAccessDeniedMessage(
          applicantAccessDeniedAgencies(accessError)
        )}
        onToggle={onToggle}
      />
    )
  }

  const fullName = formatName(data)
  const phone = data.phoneMobile || data.phoneSecondary

  return (
    <MessagingContextPanel
      onToggle={onToggle}
      header={
        <>
          <div className={applicantModalPictureWrapper}>
            <ApplicantAvatar
              applicantId={id}
              fullName={fullName}
              profilePictureUrl={picture?.profilePicUrl}
              pictureClassName={contextAvatar}
              size={56}
              source="messaging"
            />
          </div>
          <button
            type="button"
            className={clsx(contextName, contextNameLink, contextNameButton)}
            onClick={() => setApplicantCurrentId(id)}
          >
            {fullName}
          </button>
          <div className={contextHeaderActions}>
            <ApplicantStatusUpdater
              applicantId={id}
              initialStatus={data.status ?? undefined}
              ghost
            />
            {phone && <ContextPhoneButton phone={phone} />}
          </div>
        </>
      }
    >
      <div className={applicantModalAsideActions}>
        <div className={applicantModalAsideActionsTitle}>
          {currentHoverAction ?? currentAction ?? 'Actions rapides'}
        </div>
        <ActionsButtonList
          target={{ type: 'applicant', ids: [id] }}
          hiddenActions={HIDDEN_ACTIONS}
          onActionHover={setCurrentHoverAction}
          onCurrentActionChange={setCurrentAction}
          availablePositions={ACTION_POSITIONS}
          defaultPosition="bottom"
          hasEmail={!!data.email?.trim()}
        />
        <AsideActionWrapper position="bottom" />
      </div>
      <ApplicantTasks applicantId={id} />
      <ApplicantReports applicantId={id} key={`applicant-reports-${id}`} />
    </MessagingContextPanel>
  )
}
